import React, { useCallback, useContext, useState } from "react";
import { Honeybadger } from "@honeybadger-io/react";
import { useStripe, useElements } from '@stripe/react-stripe-js';

import { buildRedirectUrl } from "./redirect";
import Legal from "./Legal";
import { toMoneyString } from "../Money";
import { trackPurchase } from "./utils";
import { ToolContext } from "./ToolProvider";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";

import * as UI from "./ui";

const EXPRESS_CHECKOUT_OPTIONS = {
  emailRequired: true,
  phoneNumberRequired: true,
  billingAddressRequired: true,
  buttonType: {
    googlePay: 'donate',
    applePay: 'donate',
  },
  paymentMethodOrder: ['applePay', 'googlePay', 'link'],
};

const billingDetailsToDonor = (billingDetails) => {
  const nameParts = billingDetails.name ? billingDetails.name.split(" ") : [];
  return {
    firstName: nameParts[0],
    lastName: nameParts.slice(1).join(" "),
    email: billingDetails.email,
    phoneNumber: billingDetails.phone,
    addressLine1: billingDetails.address?.line1,
    addressLine2: billingDetails.address?.line2,
    city: billingDetails.address?.city,
    state: billingDetails.address?.state,
    zipCode: billingDetails.address?.postal_code,
    country: billingDetails.address?.country,
  }
};

const ExpressCheckout = ({ sweepstakes, setupPaymentIntent, onComplete, setDonationInProgress, setErrorMessage, fingerprint, disabled }) => {
  const [visible, setVisible] = useState(false);
  const { donationLevel, promoCode, setPaymentIntent, setDonor } = useContext(ToolContext);

  const stripe = useStripe();
  const elements = useElements();

  const handleConfirm = useCallback(async (event) => {
    try {
      setDonationInProgress(true);
      const newDonorInfo = billingDetailsToDonor(event.billingDetails);
      const paymentMethod = event.expressPaymentType;

      if (newDonorInfo.state?.toUpperCase() === "WA") {
        event.paymentFailed({
          reason: "address_unserviceable",
          message: "Sorry, but donations are not available to residents of the State of Washington."
        });
        setDonationInProgress(false);
        return;
      }

      const setupResult = await setupPaymentIntent({
        variables: {
          attributes: {
            donationLevelId: donationLevel.id,
            promoCodeName: promoCode ? promoCode.name : null,
            fingerprint,
          }
        }
      });

      const { data: { setupPaymentIntent: { paymentIntent, error } } } = setupResult;
      if (error || !paymentIntent) {
        setDonationInProgress(false);
        event.paymentFailed({
          reason: "fail",
          message: error?.message || "Could not initiate payment."
        });
        return;
      }
      const donationId = paymentIntent.donationId;

      const redirectUrl = buildRedirectUrl({
        donationId,
        paymentMethod,
        promoCodeName: promoCode?.name,
      });
      const payment = await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntent.clientSecret,
        confirmParams: {
          return_url: redirectUrl,
        },
        redirect: 'if_required',
      });

      if (payment.error) {
        setErrorMessage(payment.error.message || "Payment failed. Please try again.");
        setDonationInProgress(false);
        event.paymentFailed({
          reason: "fail",
          message: payment.error.message || "Payment failed. Please try again."
        });
        return;
      }

      const finalPaymentIntent = Object.assign(payment.paymentIntent, { donationId });
      trackPurchase({
        paymentIntent: finalPaymentIntent,
        paymentMethod,
        donationLevel,
        promoCode,
        sweepstakes,
      });
      setPaymentIntent(finalPaymentIntent);
      setDonor(newDonorInfo);
      setDonationInProgress(false);
      onComplete();
    } catch (err) {
      Honeybadger.notify(err, {
        context: {
          component: "ExpressCheckout",
          payment_method: event?.expressPaymentType,
          sweepstakesId: sweepstakes?.id,
        }
      });
      setDonationInProgress(false);
      setErrorMessage("A network error occurred. Please try again.");
    }
  }, [sweepstakes, donationLevel, promoCode, fingerprint, setupPaymentIntent, stripe, elements, setPaymentIntent, setDonor, onComplete]);

  const handleReady = useCallback(({ availablePaymentMethods }) => {
    if (Object.values(availablePaymentMethods).some(v => v)) {
      setVisible(true);
    }
  }, []);

  if (!donationLevel) {
    return null;
  }

  return  (
    <UI.ExpressCheckoutContainer $visible={visible} $disabled={disabled} inert={disabled}>
      <div>
        <ExpressCheckoutElement
          onClick={e => {
            setErrorMessage('')
            e.resolve();
          }}
          onReady={handleReady}
          onConfirm={handleConfirm}
          lineItems={[{ name: `${toMoneyString({ cents: donationLevel.price })} Donation`, amount: donationLevel.price }]}
          options={EXPRESS_CHECKOUT_OPTIONS}
        />
        <Legal />
      </div>
      <UI.ExpressCheckoutDivider>OR</UI.ExpressCheckoutDivider>
    </UI.ExpressCheckoutContainer>
  );
};

export default ExpressCheckout;
